import moment from "moment";

import { Client } from "@/Common/Api/Client";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { HyperLinkRedirectControl } from "@/Common/Components/Buttons/HyperLinkRedirectControl";
import { CommonTableHeaderComponent } from "@/Common/Components/Tables/CommonTableHeaderComponent";
import { MyleTable } from "@/Common/Components/Tables/MyleTable";
import { MyleTableColumn, MyleTableColumnInit, MyleTableColumnOptions } from "@/Common/Components/Tables/MyleTableColumn";
import { MyleTableColumnOrderControlv2 } from "@/Common/Components/Tables/MyleTableColumnOrderControlv2";
import { SimplePagerControl } from "@/Common/Components/Tables/SimplePagerControl";
import { MyleTableHelper } from "@/Common/Helpers/MyleTableHelper";
import { NameHelper } from "@/Common/Helpers/NameHelper";
import { CommunicationMessagesSortOrderType, CommunicationStateType, CommunicationsUIStateInbox, RedirectTo, SortDirection } from "@/Common/Models/UI/Enums";
import { ITableRenderOptions } from "@/Common/Models/UI/Tables/ITableRenderOptions";
import { MyleTableTarget } from "@/Common/Models/UI/Tables/MyleTableTarget";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { ISearchMessageRequest } from "@/Communications/Api/Dto/Requests/ISearchMessageRequest";
import { MessageOrTaskSearchResponseModel } from "@/Communications/Api/Dto/Responses/MessageOrTaskSearchResponseModel";
import { MessageOrTaskItem } from "@/Communications/Models/MessageOrTaskItem";
import { MessageOrTasksTableItem } from "@/Communications/Models/MessageOrTaskTableItems";
import { MessageSearchSettings } from "@/Communications/Models/Settings/MessageSearchSettings";
import { CommunicationsTab_OptimizedV2 } from "../CommunicationsTab_OptimizedV2";
import { MessageSettingsContainer_OptimizedV2, MessageSettingsContainer_OptimizedV2State } from "./MessageSearchSettingsContainer_OptimizedV2";

interface MessageSearch_OptimizedV2Props {
    inboxType: CommunicationsUIStateInbox;
    shouldReload?: boolean;

    onLoadSuccess?: () => void;
    selectedMessageRowClick: (message: MessageOrTaskItem) => void;
    showNotesPopup: (rowData: MessageOrTasksTableItem, e: React.MouseEvent) => void;
    communicationsCount?: () => void;

    savedState?: MessageSearch_OptimizedV2State;
    settingsSavedSate?: MessageSettingsContainer_OptimizedV2State;
}

class MessageSearch_OptimizedV2State {
    public messages: MessageOrTasksTableItem[];
    public searchSettings: MessageSettingsContainer_OptimizedV2State;
    public isSearchSettingsOpen: boolean;

    public searchText: string;
    public isSelectAllChecked: boolean;

    public currentPage: number;
    public totalQueryPages: number;
    public totalQueryRows: number;
    public selectedPageNumber: number;

    public isColumnOrderingPopupOpen: boolean;

    public sortingColumn: CommunicationMessagesSortOrderType;
    public sortingDirection: SortDirection;

    public static getDefault(): MessageSearch_OptimizedV2State {
        const state = new MessageSearch_OptimizedV2State();
        state.messages = [];
        state.searchText = "";
        state.currentPage = 1;
        state.selectedPageNumber = 1;
        state.totalQueryPages = 0;
        state.totalQueryRows = 0;
        state.isSearchSettingsOpen = false;

        state.searchSettings = MessageSettingsContainer_OptimizedV2State.getDefault();

        return state;
    }
}

class MessageSearch_OptimizedV2 extends BaseReactComponent<MessageSearch_OptimizedV2Props, MessageSearch_OptimizedV2State> {
    private _saveOptionKey: string;
    private _viewName: string = cacheStore.GetString("InboxLabel");

    private _unreadMessageStyle = { fontWeight: "bold" } as React.CSSProperties;

    public constructor(props: MessageSearch_OptimizedV2Props) {
        super(props);
        this.state = MessageSearch_OptimizedV2State.getDefault();
    }

    public componentDidMount(): void {
        const stateToUse = this.props.savedState || this.state;
        this.setState(stateToUse, () => this.loadMessages(this.props.inboxType));

        $(window).trigger("resize");
    }

    public componentDidUpdate(prevProps: MessageSearch_OptimizedV2Props): void {
        const inboxTypeChanged = this.props.inboxType !== prevProps.inboxType;
        const shouldReloadToggled = this.props.shouldReload && !prevProps.shouldReload;

        if (inboxTypeChanged || shouldReloadToggled) {
            this.loadMessages(this.props.inboxType);
        }
    }

    public componentWillUnmount(): void {
        setTimeout(() => sessionStore.dispatch(
            LocalOperations.Communications.CommunicationStatesSave,
            {
                type: CommunicationStateType.MessageSearch,
                state: this.state
            }
        ), 0);
    }

    public render(): JSX.Element {
        switch (this.props.inboxType) {
            case CommunicationsUIStateInbox.Inbox:
                this._viewName = cacheStore.GetString("InboxLabel");
                this._saveOptionKey = "CommunicationsMessagesGrid_INBOX";
                break;

            case CommunicationsUIStateInbox.Sent:
                this._viewName = cacheStore.GetString("SentLabel");
                this._saveOptionKey = "CommunicationsMessagesGrid_SENT";
                break;

            case CommunicationsUIStateInbox.Drafts:
                this._viewName = cacheStore.GetString("DraftsLabel");
                this._saveOptionKey = "CommunicationsMessagesGrid_DRAFTS";
                break;

            case CommunicationsUIStateInbox.Archive:
                this._viewName = cacheStore.GetString("ArchiveLabel");
                this._saveOptionKey = "CommunicationsMessagesGrid_ARCHIVE";
                break;

            case CommunicationsUIStateInbox.Trash:
                this._viewName = cacheStore.GetString("TrashLabel");
                this._saveOptionKey = "CommunicationsMessagesGrid_TRASH;";
                break;
        }

        const columnsOptions = cacheStore.Helper.loadTableColumnOptionsFromSettings(this._saveOptionKey, this.getMyleTableColumnsDefaultOptions());
        const tableColumns = this.getTableColumns(columnsOptions);
        const tableRenderOptions = this.loadTableRenderOptions(columnsOptions);
        const columnConfiguration = cacheStore.Helper.castColumnConfigurationToMyleTableColumnOptions(columnsOptions.toArray());

        let shouldShowDeleteButton = (
            this.props.inboxType !== CommunicationsUIStateInbox.Sent &&
            this.props.inboxType !== CommunicationsUIStateInbox.Trash
        );
        let shouldShowArchiveButton = this.props.inboxType === CommunicationsUIStateInbox.Inbox;
        let shouldShowRestoreButton = this.props.inboxType === CommunicationsUIStateInbox.Trash;
        let shouldShowMarkAsUnreadButton = this.props.inboxType === CommunicationsUIStateInbox.Inbox;

        const nbSelectedItems = this.state.messages.filter(x => x.IsSelected).length;
        const nbReadSelectedItems = this.state.messages.filter(x => x.IsSelected && x.Model.IsRead).length;

        return (
            <div className="workspace">
                <CommonTableHeaderComponent
                    title={`${cacheStore.GetString("MessagesLabel")} : ${this._viewName}`}
                    searchText={this.state.searchText}
                    searchPlaceholder={cacheStore.GetString("SearchInMessagesLabel")}
                    onSearch={text => this.mutateState(s => {
                        s.searchText = text;
                        s.selectedPageNumber = 1;
                        s.currentPage = 1;
                    }, () => this.loadMessages(this.props.inboxType))}
                    onRefreshClick={() => this.loadMessages(this.props.inboxType)}
                    onSettingsClick={() => this.mutateState(s => s.isSearchSettingsOpen = !this.state.isSearchSettingsOpen)}
                />
                <div id="posref" className="tb ws-scrollable">
                    {this.state.isSearchSettingsOpen && (
                        <MessageSettingsContainer_OptimizedV2
                            enableSaveSetting={this.props.inboxType == CommunicationsUIStateInbox.Inbox}
                            onSaveSettingsCallback={containerSettings => {
                                this.saveSearchSetting(containerSettings);
                                this.mutateState(
                                    s => {
                                        s.searchSettings = containerSettings;
                                        s.isSearchSettingsOpen = false;
                                        s.selectedPageNumber = 1;
                                    },
                                    () => this.loadMessages(this.props.inboxType)
                                );
                            }}
                            onCancelClick={() => this.mutateState(s => s.isSearchSettingsOpen = false)}
                            onSettingsButtonClick={() => {
                                this.mutateState(s => {
                                    s.isColumnOrderingPopupOpen = true;
                                    s.isSearchSettingsOpen = false;
                                });
                            }}
                            savedState={() => communicationsStore.communicationsTabSavedState.messageSettingContainerSavedState}
                        />
                    )}
                    <div className="myle-doc-tb main-content myle-form">
                        <MyleTable
                            TableRenderOptions={tableRenderOptions}
                            TableId="communications_message_table"
                            TableDataCy="communication-table"
                            Columns={tableColumns}
                            DataSource={this.state.messages.toImmutable()}
                            ColumnsPreferencesChangedOperation={ServerOperations.Server.Myle_UpdateUserSettings}
                            SaveOptionskeyTarget={this._saveOptionKey}
                            RowClickFunction={e => {
                                if (this.state.isSearchSettingsOpen) {
                                    this.mutateState(s => s.isSearchSettingsOpen = false);
                                    return;
                                }
                                this.props.selectedMessageRowClick(e.Model);
                            }}
                            ColumnReorderTarget={MyleTableTarget.Myle_CommunicationsMessages_Search_ColumnReorder}
                            ColumnSortTarget={MyleTableTarget.Myle_CommunicationsMessages_Search_ColumnSortClick}
                        />
                    </div>
                </div>
                <div className={CommunicationsTab_OptimizedV2.footerCSS}>
                    <div className="col-lg-6 col-md-6 col-sm-6">
                        <SimplePagerControl
                            PageSize={this.state.searchSettings.selectedPageSizeOption}
                            TotalRows={this.state.totalQueryRows}
                            CurrentPage={this.state.currentPage}
                            changePageFunction={(pageNumber: number) => {
                                this.mutateState(s => s.selectedPageNumber = pageNumber, () => this.loadMessages(this.props.inboxType));
                            }}
                        />
                    </div>
                    <div className="col-lg-6 col-md-6 col-sm-6 text-right">
                        {shouldShowDeleteButton && (
                            <button
                                data-cy="messageSearch-delete"
                                disabled={nbSelectedItems <= 0}
                                className="btn btn-danger push-right-5"
                                onClick={() => this.onDeleteClick()}
                            >
                                {cacheStore.GetString("DeleteLabel")}
                            </button>
                        )}
                        {shouldShowArchiveButton && (
                            <button
                                data-cy="messageSearch-archive"
                                disabled={nbSelectedItems <= 0}
                                className="btn btn-primary push-right-5"
                                onClick={() => this.onArchiveClick()}
                            >
                                {cacheStore.GetString("ArchiveButtonLabel")}
                            </button>
                        )}
                        {shouldShowRestoreButton && (
                            <button
                                data-cy="messageSearch-restore"
                                disabled={nbSelectedItems <= 0}
                                className="btn btn-primary"
                                onClick={() => this.onRestoreClick()}
                            >
                                {cacheStore.GetString("RestoreLabel")}
                            </button>
                        )}
                        {shouldShowMarkAsUnreadButton && (
                            <button
                                disabled={nbReadSelectedItems <= 0}
                                className="btn btn-default push-right-5"
                                onClick={() => this.onMarkAsUnreadClick()}
                            >
                                {cacheStore.GetString("MarkAsUnreadLabel")}
                            </button>
                        )}
                    </div>
                </div>
                {this.state.isColumnOrderingPopupOpen && (
                    <MyleTableColumnOrderControlv2
                        TableColumns={columnConfiguration}
                        SaveOptionskeyTarget={this._saveOptionKey}
                        ContainerId="columnSettingsPopup"
                        closeColumnOrderPopup={() =>
                            this.mutateState(s => s.isColumnOrderingPopupOpen = false)
                        }
                    />
                )}
            </div>
        );
    }

    private getMyleTableColumnsDefaultOptions(): MyleTableColumnOptions[] {
        return [
            new MyleTableColumnOptions({
                HeaderText: "",
                FieldName: "Checkbox",
                WidthPixels: 38,
                IsFixedInPlace: true
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("SubjectLabel"),
                FieldName: "Subject",
                RowClickEnabled: true,
                IsSortable: true,
                SortColumn: CommunicationMessagesSortOrderType.Subject,
                SortDirection: SortDirection.ASC,
                WidthPercentageDefault: 15,
                sortingMethod: () => this.sortArrayBy(CommunicationMessagesSortOrderType.Subject)
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("FromLabel"),
                FieldName: "From",
                RowClickEnabled: true,
                IsSortable: true,
                SortColumn: CommunicationMessagesSortOrderType.From,
                SortDirection: SortDirection.ASC,
                WidthPercentageDefault: 30,
                sortingMethod: () => this.sortArrayBy(CommunicationMessagesSortOrderType.From)
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("ToLabel"),
                FieldName: "To",
                RowClickEnabled: true,
                IsSortable: true,
                SortColumn: CommunicationMessagesSortOrderType.To,
                SortDirection: SortDirection.ASC,
                WidthPercentageDefault: 10,
                sortingMethod: () => this.sortArrayBy(CommunicationMessagesSortOrderType.To)
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("DateLabel"),
                FieldName: "Date",
                RowClickEnabled: true,
                IsSortable: true,
                SortColumn: CommunicationMessagesSortOrderType.Date,
                SortDirection: SortDirection.ASC,
                WidthPercentageDefault: 10,
                sortingMethod: () => this.sortArrayBy(CommunicationMessagesSortOrderType.Date)
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("lblDSQPriority"),
                FieldName: "Priority",
                RowClickEnabled: true,
                IsSortable: true,
                SortColumn: CommunicationMessagesSortOrderType.Priority,
                SortDirection: SortDirection.ASC,
                WidthPercentageDefault: 10,
                sortingMethod: () => this.sortArrayBy(CommunicationMessagesSortOrderType.Priority)
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("PatientLabel"),
                FieldName: "Patient",
                RowClickEnabled: false,
                IsSortable: true,
                SortColumn: CommunicationMessagesSortOrderType.Patient,
                SortDirection: SortDirection.ASC,
                WidthPercentageDefault: 10,
                sortingMethod: () => this.sortArrayBy(CommunicationMessagesSortOrderType.Patient)
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("ActionLabel"),
                FieldName: "Action",
                RowClickEnabled: false,
                IsSortable: false,
                IsFixedInPlace: true,
                columnClass: "align-center",
                WidthPixels: 80
            })
        ];
    }

    private sortArrayBy(column: CommunicationMessagesSortOrderType): void {
        this.mutateState(s => {
            s.sortingColumn = column;
            s.sortingDirection = MyleTableHelper.getNewSortDir(column, s.sortingColumn, s.sortingDirection);
            s.messages = s.messages
                .sortByProperty(
                    message => {
                        let x = message.Model;

                        switch (column) {
                            case CommunicationMessagesSortOrderType.Subject:
                                return (x.Subject || "").toUpperCase();

                            case CommunicationMessagesSortOrderType.Date:
                                return x.SendDate;

                            case CommunicationMessagesSortOrderType.From:
                                return x.FromContact && cacheStore.GetCliniciansPersonsByContactId(x.FromContact.Id).Name.toUpperCase();

                            case CommunicationMessagesSortOrderType.To:
                                return x.ToContact && cacheStore.GetCliniciansPersonsByContactId(x.ToContact[0].Id).Name.toUpperCase();

                            case CommunicationMessagesSortOrderType.Priority:
                                return cacheStore.GetLocalizedValueByLookupId(x.PriorityLookupId).toUpperCase();

                            case CommunicationMessagesSortOrderType.Patient:
                                return x.PatientLinks && x.PatientLinks.length > 0 ? x.PatientLinks[0].LastName.toUpperCase() : "";

                            default:
                                return x.DueDate;
                        }
                    },
                    this.state.sortingDirection === SortDirection.DESC
                );
        });
    }

    private getTableColumns(columnsOptions: Immutable.List<MyleTableColumnOptions>): Immutable.List<MyleTableColumn> {
        return MyleTableColumn.buildList<MessageOrTasksTableItem>(
            columnsOptions,
            column => {
                switch (column.FieldName) {
                    case "Checkbox":
                        return this.buildCheckboxColumn();

                    case "Subject":
                        return this.buildSubjectColumn();

                    case "From":
                        return this.buildFromColumn();

                    case "To":
                        return this.buildToColumn();

                    case "Date":
                        return this.buildDateColumn();

                    case "Priority":
                        return this.buildPriorityColumn();

                    case "Patient":
                        return this.buildPatientColumn();

                    case "Action":
                        return this.buildActionColumn();
                }
            },
            false
        );
    }

    private loadTableRenderOptions(columnOptions: Immutable.List<MyleTableColumnOptions>): ITableRenderOptions {
        let defaultTablePluginOptions = {
            MinColWidth: 60,
            MinTableWidth: 980,
            Cols: columnOptions.filter(x => x.IsHidden === false).map(i => i.WidthPercentage),
            FirstColumnNoDrag: true,
            LastColumnNoDrag: true,
            Autoresize: true,
            OnColumnWidthChange: null
        };

        return Immutable.fromJS(defaultTablePluginOptions);
    }

    private buildCheckboxColumn(): MyleTableColumnInit<MessageOrTasksTableItem> {
        return {
            renderCell: item => {
                if (item.Model.IsActionPossible) {
                    return (
                        <div>
                            <input
                                type="checkbox"
                                id={item.Model.Id}
                                onChange={() => this.setSelectedMessage(item)}
                                checked={item.IsSelected}
                            />
                        </div>
                    );
                }
                else {
                    return (
                        <div />
                    );
                }
            },
            renderHeader: () => {
                if (this.props.inboxType !== CommunicationsUIStateInbox.Sent) {
                    return (
                        <div>
                            <input
                                type="checkbox"
                                onChange={() => this.setSelectedToAllMessages()}
                                checked={this.state.isSelectAllChecked}
                            />
                        </div>
                    );
                }
                else
                    return (<div />);
            }
        };
    }

    private buildSubjectColumn(): MyleTableColumnInit<MessageOrTasksTableItem> {
        return {
            renderCell: item => this.renderRowCommon(item.Model.Subject, item)
        };
    }

    private buildFromColumn(): MyleTableColumnInit<MessageOrTasksTableItem> {
        return {
            renderCell: item => this.renderRowCommon(item.Model.FromContact.LastName + " " + item.Model.FromContact.FirstName, item)
        };
    }

    private buildToColumn(): MyleTableColumnInit<MessageOrTasksTableItem> {
        return {
            renderCell: rowData => {
                let toContacts = rowData.Model.ToContact;
                let arrToContact = [];

                if (toContacts.length > 2) {
                    arrToContact = [
                        <div key="toContact" title="recipients">
                            {toContacts.length + " " + cacheStore.GetString("RecipientLabel") + "s"}
                        </div>
                    ];
                }
                else {
                    arrToContact = toContacts.map((val, idx) => {
                        let empElement = (
                            <div key={`toContact_${idx}`} title={val.LastName + ", " + val.FirstName}>{val.LastName + ", " + val.FirstName}</div>
                        );
                        return empElement;
                    });
                }

                if (rowData.Model.IsRead) {
                    return (
                        <div>{arrToContact} </div>
                    );
                }
                else {
                    return (
                        <div style={this._unreadMessageStyle}>{arrToContact} </div>
                    );
                }
            }
        };
    }

    private buildDateColumn(): MyleTableColumnInit<MessageOrTasksTableItem> {
        return {
            renderCell: item => {
                const date = moment(item.Model.SendDate).format("YYYY/MM/DD");
                const time = moment(item.Model.SendDate).format("HH:mm:ss A");
                const messageDate = date + " " + cacheStore.GetString("AtLabel") + " " + time;

                return this.renderRowCommon(messageDate, item);
            }
        };
    }

    private buildPriorityColumn(): MyleTableColumnInit<MessageOrTasksTableItem> {
        return {
            renderCell: item => {
                const priorityLookupId = item.Model.PriorityLookupId;

                let priority = "";
                if (priorityLookupId != null) {
                    const lookupPriority = cacheStore.GetLookupById(priorityLookupId);
                    if (lookupPriority) {
                        priority = lookupPriority.GetValue();
                    }
                }

                return this.renderRowCommon(priority, item);
            }
        };
    }

    private buildPatientColumn(): MyleTableColumnInit<MessageOrTasksTableItem> {
        return {
            renderCell: rowData => {
                const patients = rowData.Model.PatientLinks;

                const patientsArray = patients.map((val, idx) => {
                    let patientElement = (
                        <div
                            key={`patient_${idx}`}
                            title={NameHelper.formatFullNameForHasName(val)}
                        >
                            <HyperLinkRedirectControl
                                target={RedirectTo.Patient}
                                redirectValue={val.Id}
                                text={NameHelper.formatFullNameForHasName(val)}
                            />
                        </div>
                    );
                    return patientElement;
                });

                return (
                    <div>{patientsArray} </div>
                );
            }
        };
    }

    private buildActionColumn(): MyleTableColumnInit<MessageOrTasksTableItem> {
        return {
            renderCell: rowData => {
                if (rowData.Model.TotalNotes > 0) {
                    return (
                        <a onClick={e => this.props.showNotesPopup(rowData, e)}>
                            [
                            <span className="red-text">{rowData.Model.TotalNotes}</span>
                            ]
                        </a>
                    );
                }
                else {
                    return (
                        <a onClick={e => this.props.showNotesPopup(rowData, e)}>
                            [ <span>+</span> ]
                        </a>
                    );
                }
            }
        };
    }

    private renderRowCommon(text: string, item: MessageOrTasksTableItem): JSX.Element {
        if (item.Model.IsRead) {
            return (
                <div>{text} </div>
            );
        }
        else {
            return (
                <div style={this._unreadMessageStyle}>{text} </div>
            );
        }
    }

    private setSelectedMessage(message: MessageOrTasksTableItem): void {
        this.mutateState(s => {
            s.messages = this.state.messages;
            s.messages.forEach(c => {
                if (c.Model.IMS_ID === message.Model.IMS_ID) {
                    c.IsSelected = !c.IsSelected;
                }
            });
        });
    }

    private setSelectedToAllMessages(): void {
        this.mutateState(s => {
            s.messages = this.state.messages;
            s.messages.forEach(c => c.IsSelected = !this.state.isSelectAllChecked);
            s.isSelectAllChecked = !this.state.isSelectAllChecked;
        });
    }

    private loadMessages(inboxType: CommunicationsUIStateInbox): void {
        let searchSettings = this.state.searchSettings;
        let requestData: ISearchMessageRequest = {
            PracticianIds: searchSettings.clinicians ? searchSettings.clinicians.filter(x => x.Checked).map(x => x.Id) : [],
            SharedWithIds: searchSettings.sharedWith.filter(x => x.Checked).map(x => x.Id),
            PriorityIds: searchSettings.priorities.filter(x => x.Checked).map(x => x.Id),
            FromDueDate: searchSettings.dateFrom ? cacheStore.Helper.CreateDateAsUTC(searchSettings.dateFrom) : null,
            ToDueDate: searchSettings.dateTo ? cacheStore.Helper.CreateDateAsUTC(searchSettings.dateTo) : null,
            PageNumber: this.state.selectedPageNumber,
            PageSize: searchSettings.selectedPageSizeOption,
            SearchString: this.state.searchText,
            IsTask: false,
            OrderByPriorityFirst: searchSettings.orderByPriorityFirst
        };

        switch (inboxType) {
            case CommunicationsUIStateInbox.Drafts:
                delete requestData.PracticianIds;
                delete requestData.IsArchive;
                requestData.IsTrash = false;
                requestData.IsDraft = true;
                requestData.IsSent = true;
                requestData.UIState = CommunicationsUIStateInbox.Drafts;
                break;

            case CommunicationsUIStateInbox.Sent:
                delete requestData.IsTrash;
                delete requestData.IsArchive;
                requestData.IsDraft = false;
                requestData.IsSent = true;
                requestData.UIState = CommunicationsUIStateInbox.Sent;
                break;

            case CommunicationsUIStateInbox.Trash:
                requestData.IsTrash = true;
                delete requestData.IsArchive;
                requestData.IsDraft = false;
                requestData.IsSent = false;
                requestData.UIState = CommunicationsUIStateInbox.Trash;
                break;

            case CommunicationsUIStateInbox.Archive:
                requestData.IsTrash = false;
                requestData.IsArchive = true;
                delete requestData.IsDraft;
                requestData.IsSent = false;
                requestData.UIState = CommunicationsUIStateInbox.Archive;
                break;

            case CommunicationsUIStateInbox.Inbox:
            default:
                requestData.IsTrash = false;
                requestData.IsArchive = false;
                requestData.IsDraft = false;
                requestData.IsSent = false;
                requestData.UIState = CommunicationsUIStateInbox.Inbox;
                break;
        }

        Client.Postv2(
            ServerOperations.Server.Myle_SearchMessagesOrTasks,
            requestData,
            response => {
                if (response) {
                    this.props.communicationsCount && this.props.communicationsCount();
                    const model: MessageOrTaskSearchResponseModel = response;

                    this.mutateState(s => {
                        s.messages = model.Messages
                            .map(messageOrTaskResponse => new MessageOrTasksTableItem(messageOrTaskResponse));

                        s.currentPage = model.CurrentPage;
                        s.totalQueryPages = model.TotalQueryPages;
                        s.totalQueryRows = model.TotalQueryRows;

                        s.isSelectAllChecked = false;
                    }, () => this.props.onLoadSuccess && this.props.onLoadSuccess());
                }
            }
        );
    }

    private onDeleteClick(): void {
        Client.Postv2(
            ServerOperations.Server.Myle_SetMessageState,
            {
                isDraftOwner: this.props.inboxType == CommunicationsUIStateInbox.Drafts,
                individualMessageIds: this.state.messages.filter(x => x.IsSelected).map(x => x.Model.IM_ID),
                isTrash: true
            },
            response => {
                if (response.Success) {
                    this.loadMessages(this.props.inboxType);
                }
            }
        );
    }

    private onArchiveClick(): void {
        Client.Postv2(
            ServerOperations.Server.Myle_SetMessageState,
            {
                isDraftOwner: this.props.inboxType == CommunicationsUIStateInbox.Drafts,
                individualMessageIds: this.state.messages.filter(x => x.IsSelected).map(x => x.Model.IM_ID),
                isArchive: true
            },
            response => {
                if (response.Success) {
                    this.loadMessages(this.props.inboxType);
                }
            }
        );
    }

    private onRestoreClick(): void {
        Client.Postv2(
            ServerOperations.Server.Myle_SetMessageState,
            {
                isDraftOwner: this.props.inboxType == CommunicationsUIStateInbox.Drafts,
                individualMessageIds: this.state.messages.filter(x => x.IsSelected).map(x => x.Model.IM_ID),
                isTrash: false,
                isArchive: false
            },
            response => {
                if (response.Success) {
                    this.loadMessages(this.props.inboxType);
                }
            }
        );
    }
    private onMarkAsUnreadClick(): void {
        const messageIds = this.state.messages.filter(m => m.IsSelected).map(m => m.Model.IM_ID);

        Client.Postv2(
            ServerOperations.Server.Myle_MarkMessagesAsUnRead,
            {
                MessageIds: messageIds
            },
            response => {
                if (response) {
                    this.loadMessages(this.props.inboxType);
                }
            }
        );
    }

    private saveSearchSetting(model: MessageSettingsContainer_OptimizedV2State): void {
        if (model.isSaveSettingChecked) {
            let setting = MessageSearchSettings.FromModel(model);
            let userSetting = cacheStore.UserSettings;
            userSetting.MessageSearchSettings = setting;
            cacheStore.UserSettings = userSetting;
            Client.Postv2(
                ServerOperations.Server.Myle_UpdateUserSettings,
                {
                    UserSettings: userSetting,
                    UserId: sessionStore.userId
                },
                () => { }
            );
        }
    }
}

export { MessageSearch_OptimizedV2, MessageSearch_OptimizedV2State };
